/**
 * Re-send / re-schedule of a KILLED drop — the timing rules both sides share.
 * For Media Devoted | Created By Ciro Autiero
 *
 * SMSWiser cannot restart a stopped drop, so a re-send is a NEW drop with the
 * same settings. Its start must sit inside SMSWiser's 24 h window (less the
 * 5 min margin the send path keeps), and the dialog that offers it and the
 * endpoint that runs it must agree on exactly where that window ends.
 */

import {
  PRESET_SCHEDULE_TIMEZONE,
  shiftScheduleTemplateToWorkDate,
} from '../schedule-template-shift';

/** A scheduled re-send must start at least this far ahead of now. */
export const RESEND_MIN_LEAD_MS = 60_000;
/** SMSWiser rejects a start more than 24 h out; the send path keeps 5 min spare. */
export const RESEND_MAX_AHEAD_MS = 24 * 60 * 60_000 - 5 * 60_000;

export type ResendScheduleIssue = 'invalid' | 'past' | 'too-far';

const DAY_MS = 86_400_000;

/** Why a scheduled start cannot be used, or null when it can. */
export function resendScheduleIssue(
  at: Date,
  now: Date = new Date(),
): ResendScheduleIssue | null {
  const t = at.getTime();
  if (Number.isNaN(t)) return 'invalid';
  if (t < now.getTime() + RESEND_MIN_LEAD_MS) return 'past';
  if (t > now.getTime() + RESEND_MAX_AHEAD_MS) return 'too-far';
  return null;
}

/** The latest start SMSWiser still accepts, floored to the minute. */
export function latestResendStart(now: Date = new Date()): Date {
  const latest = now.getTime() + RESEND_MAX_AHEAD_MS;
  return new Date(Math.floor(latest / 60_000) * 60_000);
}

/** "YYYY-MM-DD" of an instant on the schedule zone's calendar. */
export function ymdInScheduleZone(
  at: Date,
  zone: string = PRESET_SCHEDULE_TIMEZONE,
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

/**
 * The next time the drop's own planned wall clock comes round: today at 19:35
 * while that is still ahead, else tomorrow at 19:35. A clock time that passed
 * under five minutes ago lands just past the window, so it is pulled back to
 * the window's last minute rather than refused.
 */
export function nextOriginalResendTime(
  plannedAt: Date,
  now: Date = new Date(),
): Date {
  const onDay = (ymd: string) =>
    shiftScheduleTemplateToWorkDate({
      templateAt: plannedAt,
      workDateYmd: ymd,
      nowUtc: new Date(0),
    });
  const today = ymdInScheduleZone(now);
  let next = onDay(today);
  if (next.getTime() < now.getTime() + RESEND_MIN_LEAD_MS) {
    next = onDay(addDaysYmd(today, 1));
  }
  const latest = latestResendStart(now);
  return next.getTime() > latest.getTime() ? latest : next;
}

/**
 * The work day a re-sent drop belongs to. Going out on the calendar day it was
 * planned for, it stays on its own work day — a late send planned past
 * midnight keeps its +1. Going out on any other day, it is that day's drop: a
 * drop killed on Monday and re-sent on Wednesday is Wednesday's.
 */
export function resendWorkDate(params: {
  sourceWorkDateYmd: string;
  plannedAt: Date | null;
  sendAt: Date;
}): string {
  const sendDay = ymdInScheduleZone(params.sendAt);
  const planned = params.plannedAt;
  if (planned && !Number.isNaN(planned.getTime())) {
    if (ymdInScheduleZone(planned) === sendDay) {
      return params.sourceWorkDateYmd.slice(0, 10);
    }
  }
  return sendDay;
}
